import { z } from 'zod';
import { IdSchema } from './encoding.js';
/**
 * Purchasing contracts: purchase requests, purchase orders and goods receipts (ERD §7.2;
 * FRD-PRC-017 to 034, PRC-039 to 042, PRC-064).
 *
 * Quantities, prices and amounts cross the wire as **decimal strings**, never numbers
 * (SDD §9.2, §10.3). A request may send up to 4 decimals for a quantity or a price; responses carry
 * quantities and prices at scale 4 and amounts at scale 2.
 */
const QuantityInputSchema = z
    .string()
    .regex(/^\d{1,15}(\.\d{1,4})?$/, 'must be a decimal quantity with at most 4 decimals');
const PriceInputSchema = z
    .string()
    .regex(/^\d{1,15}(\.\d{1,4})?$/, 'must be a decimal price with at most 4 decimals');
const DateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');
const text = (max) => z.string().trim().min(1).max(max);
export const PURCHASE_REQUEST_STATUSES = [
    'draft',
    'submitted',
    'approved',
    'rejected',
    'returned',
    'converted',
    'cancelled',
];
export const PURCHASE_ORDER_STATUSES = [
    'draft',
    'submitted',
    'approved',
    'rejected',
    'returned',
    'issued',
    'partially_received',
    'received',
    'closed',
    'cancelled',
];
export const PurchaseOrderOriginSchema = z.enum(['purchase_request', 'emergency']);
// ------------------------------------------------------------------------ purchase requests
/** One requisition line (FRD-PRC-018 to 020, PRC-026). Dimensions only where given. */
export const PurchaseRequestLineRequestSchema = z.strictObject({
    description: text(500),
    quantity: QuantityInputSchema,
    estimatedUnitCost: PriceInputSchema,
    requiredByDate: DateSchema,
    categoryCode: z.string().min(1).max(100).optional(),
    isAsset: z.boolean(),
    assetClassId: IdSchema.optional(),
    accountId: IdSchema,
    costCenterId: IdSchema.optional(),
    projectId: IdSchema.optional(),
    assetId: IdSchema.optional(),
    taxCodeId: IdSchema.optional(),
    budgetReference: text(200).optional(),
});
export const CreatePurchaseRequestRequestSchema = z.strictObject({
    /** Omitted: the caller's own employee (self-service, FRD-PRC-021). */
    requesterEmployeeId: IdSchema.optional(),
    requestDate: DateSchema,
    justification: text(2000),
    preferredSupplierId: IdSchema.optional(),
    lines: z.array(PurchaseRequestLineRequestSchema).min(1).max(200),
});
/** The whole editable content, replaced as one (the requester is fixed once raised). */
export const UpdatePurchaseRequestRequestSchema = CreatePurchaseRequestRequestSchema.omit({
    requesterEmployeeId: true,
});
export const PurchaseRequestSchema = z.object({
    id: IdSchema,
    documentNo: z.string(),
    requesterEmployeeId: IdSchema,
    requesterLabel: z.string().nullable(),
    requestDate: z.string(),
    justification: z.string(),
    preferredSupplierId: IdSchema.nullable(),
    preferredSupplierLabel: z.string().nullable(),
    departmentId: IdSchema,
    costCenterId: IdSchema,
    locationId: IdSchema,
    status: z.string(),
    estimatedTotal: z.string(),
    lineCount: z.number().int(),
    createdAt: z.string(),
    version: z.number().int(),
});
export const PurchaseRequestLineSchema = z.object({
    id: IdSchema,
    lineNo: z.number().int(),
    description: z.string(),
    quantity: z.string(),
    estimatedUnitCost: z.string(),
    estimatedLineCost: z.string(),
    requiredByDate: z.string(),
    categoryCode: z.string().nullable(),
    isAsset: z.boolean(),
    assetClassId: IdSchema.nullable(),
    assetClassLabel: z.string().nullable(),
    accountId: IdSchema,
    accountCode: z.string().nullable(),
    accountLabel: z.string().nullable(),
    costCenterId: IdSchema.nullable(),
    costCenterLabel: z.string().nullable(),
    projectId: IdSchema.nullable(),
    projectLabel: z.string().nullable(),
    assetId: IdSchema.nullable(),
    assetLabel: z.string().nullable(),
    taxCodeId: IdSchema.nullable(),
    taxCodeLabel: z.string().nullable(),
    budgetReference: z.string().nullable(),
    /** On order lines of purchase orders that are not cancelled (FRD-PRC-024). */
    convertedQuantity: z.string(),
    remainingQuantity: z.string(),
});
/** A related record by its number and status, linked from the page that shows it. */
export const PurchasingRefSchema = z.object({
    id: IdSchema,
    documentNo: z.string(),
    status: z.string(),
});
export const PurchaseRequestDetailSchema = z.object({
    request: PurchaseRequestSchema,
    departmentLabel: z.string().nullable(),
    costCenterLabel: z.string().nullable(),
    lines: z.array(PurchaseRequestLineSchema),
    /** The purchase orders converting this request (FRD-PRC-024, PRC-063). */
    purchaseOrders: z.array(PurchasingRefSchema),
});
/** The purchase request category values (ERD §7.2), inactive ones included for history. */
export const ProcurementListsSchema = z.object({
    prCategory: z.array(z.object({ valueCode: z.string(), label: z.string(), active: z.boolean() })),
});
// -------------------------------------------------------------------------- purchase orders
/** One order line (FRD-PRC-027). The VAT is determined by the tax module, never sent. */
export const PurchaseOrderLineRequestSchema = z.strictObject({
    /** The request line it converts; required on an order from a request, absent on an emergency. */
    purchaseRequestLineId: IdSchema.optional(),
    description: text(500),
    quantity: QuantityInputSchema,
    unitPrice: PriceInputSchema,
    accountId: IdSchema,
    costCenterId: IdSchema.optional(),
    projectId: IdSchema.optional(),
    assetId: IdSchema.optional(),
});
const PurchaseOrderContentSchema = z.strictObject({
    /** Omitted on an order from a request: the request's preferred supplier. */
    supplierId: IdSchema.optional(),
    poDate: DateSchema,
    deliveryDate: DateSchema.optional(),
    pointOfDelivery: text(500).optional(),
    contactPerson: text(200).optional(),
    termsAndConditions: text(5000).optional(),
    paymentTerms: text(500).optional(),
    /** Mandatory on the emergency exception, refused otherwise (FRD-PRC-025). */
    emergencyReason: text(2000).optional(),
    lines: z.array(PurchaseOrderLineRequestSchema).min(1).max(200),
});
export const CreatePurchaseOrderRequestSchema = PurchaseOrderContentSchema.extend({
    origin: PurchaseOrderOriginSchema,
    purchaseRequestId: IdSchema.optional(),
});
/** The whole editable content, replaced as one. The origin and request are fixed at creation. */
export const UpdatePurchaseOrderRequestSchema = PurchaseOrderContentSchema;
export const PurchaseOrderSchema = z.object({
    id: IdSchema,
    documentNo: z.string(),
    poType: z.string(),
    origin: PurchaseOrderOriginSchema,
    purchaseRequestId: IdSchema.nullable(),
    purchaseRequestNo: z.string().nullable(),
    emergencyReason: z.string().nullable(),
    supplierId: IdSchema,
    supplierLabel: z.string().nullable(),
    poDate: z.string(),
    deliveryDate: z.string().nullable(),
    pointOfDelivery: z.string().nullable(),
    contactPerson: z.string().nullable(),
    termsAndConditions: z.string().nullable(),
    paymentTerms: z.string().nullable(),
    departmentId: IdSchema.nullable(),
    costCenterId: IdSchema.nullable(),
    locationId: IdSchema.nullable(),
    status: z.string(),
    issuedAt: z.string().nullable(),
    issuedBy: IdSchema.nullable(),
    issuedByLabel: z.string().nullable(),
    netTotal: z.string(),
    vatTotal: z.string(),
    grossTotal: z.string(),
    receivedValue: z.string(),
    outstandingValue: z.string(),
    createdAt: z.string(),
    version: z.number().int(),
});
export const PurchaseOrderLineSchema = z.object({
    id: IdSchema,
    lineNo: z.number().int(),
    purchaseRequestLineId: IdSchema.nullable(),
    purchaseRequestLineNo: z.number().int().nullable(),
    /** The asset class a candidate capital asset line carries from its request line (FRD-AST-017). */
    assetClassId: IdSchema.nullable(),
    description: z.string(),
    quantity: z.string(),
    unitPrice: z.string(),
    lineTotal: z.string(),
    taxCodeId: IdSchema,
    taxCodeLabel: z.string().nullable(),
    estimatedVat: z.string(),
    accountId: IdSchema,
    accountCode: z.string().nullable(),
    accountLabel: z.string().nullable(),
    costCenterId: IdSchema.nullable(),
    costCenterLabel: z.string().nullable(),
    projectId: IdSchema.nullable(),
    projectLabel: z.string().nullable(),
    assetId: IdSchema.nullable(),
    assetLabel: z.string().nullable(),
    receivedQuantity: z.string(),
    outstandingQuantity: z.string(),
    receivedValue: z.string(),
    outstandingValue: z.string(),
});
export const GoodsReceiptRefSchema = z.object({
    id: IdSchema,
    documentNo: z.string(),
    receiptDate: z.string(),
    status: z.string(),
});
export const PurchaseOrderDetailSchema = z.object({
    order: PurchaseOrderSchema,
    lines: z.array(PurchaseOrderLineSchema),
    /** The originating request (FRD-PRC-024, PRC-063); null on the emergency exception. */
    purchaseRequest: PurchasingRefSchema.nullable(),
    receipts: z.array(GoodsReceiptRefSchema),
});
// -------------------------------------------------------------------------- goods receipts
export const CreateGoodsReceiptRequestSchema = z.strictObject({
    purchaseOrderId: IdSchema,
    receiptDate: DateSchema,
    lines: z
        .array(z.strictObject({
        purchaseOrderLineId: IdSchema,
        quantityReceived: QuantityInputSchema,
    }))
        .min(1)
        .max(200),
});
export const GoodsReceiptSchema = z.object({
    id: IdSchema,
    documentNo: z.string(),
    purchaseOrderId: IdSchema,
    purchaseOrderNo: z.string(),
    supplierId: IdSchema,
    supplierLabel: z.string().nullable(),
    receiptDate: z.string(),
    receivedBy: IdSchema,
    receivedByLabel: z.string().nullable(),
    status: z.string(),
    lineCount: z.number().int(),
    createdAt: z.string(),
});
export const GoodsReceiptDetailSchema = z.object({
    receipt: GoodsReceiptSchema,
    lines: z.array(z.object({
        id: IdSchema,
        lineNo: z.number().int(),
        purchaseOrderLineId: IdSchema,
        purchaseOrderLineNo: z.number().int(),
        description: z.string(),
        quantityReceived: z.string(),
    })),
    purchaseOrderStatus: z.string(),
});
//# sourceMappingURL=purchasing.js.map